import { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const AuthContext = createContext(null);

const TOKEN_KEY = "haevn:auth_token";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Wire token into axios + load user on mount
    useEffect(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
            setLoading(false);
            return;
        }
        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        api.get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => {
                localStorage.removeItem(TOKEN_KEY);
                delete api.defaults.headers.common.Authorization;
            })
            .finally(() => setLoading(false));
    }, []);

    // Global 401 handler — clear session and let routes redirect to /login.
    useEffect(() => {
        const interceptor = api.interceptors.response.use(
            (r) => r,
            (err) => {
                if (err?.response?.status === 401) {
                    localStorage.removeItem(TOKEN_KEY);
                    delete api.defaults.headers.common.Authorization;
                    setUser(null);
                    // Avoid redirect loop while already on /login
                    if (typeof window !== "undefined"
                        && !window.location.pathname.startsWith("/login")) {
                        window.location.href = "/login";
                    }
                }
                return Promise.reject(err);
            },
        );
        return () => api.interceptors.response.eject(interceptor);
    }, []);

    const login = async (email, password) => {
        const r = await api.post("/auth/login", { email, password });
        const token = r.data.access_token;
        localStorage.setItem(TOKEN_KEY, token);
        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        setUser(r.data.user);
        return r.data.user;
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        delete api.defaults.headers.common.Authorization;
        setUser(null);
        if (typeof window !== "undefined") window.location.href = "/login";
    };

    const isAdmin = user?.role === "admin";

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, isAdmin }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
